import React, { createContext, useContext, useState, ReactNode } from "react";
import Modal from "./Modal";

type ButtonStyles = React.CSSProperties & {
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
};

type ModalConfig = {
  id: string;
  title?: string;
  content: ReactNode;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean; // true by default, set to false to prevent closing
  primaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonStyles?: ButtonStyles;
  secondaryButtonStyles?: ButtonStyles;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  primaryButtonColor?: "primary" | "secondary" | "warning" | "error";
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false | string | number;
  fullWidth?: boolean;
  contentPadding?: string;
  containerPadding?: string;
  width?: string | number;
  height?: string | number;
  minWidth?: string | number;
  minHeight?: string | number;
  maxHeight?: string | number;
};

type ModalContextType = {
  openModal: (config: ModalConfig) => void;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  updateModal: (id: string, updates: Partial<Omit<ModalConfig, "id" | "content">>) => void;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

type ModalProviderProps = {
  children: ReactNode;
};

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modals, setModals] = useState<ModalConfig[]>([]);

  const openModal = (config: ModalConfig) => {
    setModals((prev) => [...prev.filter((m) => m.id !== config.id), config]);
  };

  const closeModal = (id: string) => {
    setModals((prev) => prev.filter((m) => m.id !== id));
  };

  const closeAllModals = () => {
    setModals([]);
  };

  const updateModal = (
    id: string,
    updates: Partial<Omit<ModalConfig, "id" | "content">>
  ) => {
    setModals((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...updates } : m))
    );
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, closeAllModals, updateModal }}>
      {children}
      {modals.map((modal) => (
        <Modal
          key={modal.id}
          open={true}
          onClose={() => closeModal(modal.id)}
          title={modal.title}
          showCloseButton={modal.showCloseButton}
          closeOnBackdropClick={modal.closeOnBackdropClick}
          primaryButtonText={modal.primaryButtonText}
          primaryButtonStyles={modal.primaryButtonStyles}
          secondaryButtonStyles={modal.secondaryButtonStyles}
          secondaryButtonText={modal.secondaryButtonText}
          onPrimaryClick={modal.onPrimaryClick}
          onSecondaryClick={modal.onSecondaryClick}
          maxWidth={modal.maxWidth}
          fullWidth={modal.fullWidth}
          contentPadding={modal.contentPadding}
          containerPadding={modal.containerPadding}
          width={modal.width}
          height={modal.height}
          minWidth={modal.minWidth}
          minHeight={modal.minHeight}
          maxHeight={modal.maxHeight}
        >
          {modal.content}
        </Modal>
      ))}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
